import React from 'react';

import Locale from 'data/locale';
import EditingContext from 'store/editingContext';
import Store from 'store';
import History from 'store/history';
import Session from 'store/session';

import './ImportPane.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFolder, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { exportAllProfiles, exportProfile } from 'logic/import';
import useApi from 'api';
import Select, { underlinerFunc } from 'ui/select';
import { foldersFix } from './Profiles';
import { getProfilePath } from 'common';

const _L = Locale("ui-import.js");

function profilesAdd(profiles, data, id, folder) {
  if (!profiles) return profiles;

  data = {
    id: id.toString(),
    value: data.name,
    class: data.class,
    folder,
    date: Math.floor(Date.now() / 1000),
  };

  profiles = profiles.slice();
  const index = profiles.findIndex(profile => parseInt(profile.id) === id);
  if (index >= 0) {
    profiles[index] = data;
  } else {
    profiles.push(data);
  }
  return profiles;
}

const renderFolder = ({id, name, depth}, search) => (
  <span style={{paddingLeft: 8 * depth}}>
    <FontAwesomeIcon icon={faFolder}/> {search ? underlinerFunc(name, search) : name}
  </span>
);

const FolderSelect = React.memo(function FolderSelect({ folder, setFolder }) {
  const folders = Session.useSelector(store => store.folders);
  const dispatch = Session.useDispatch();
  const api = useApi();

  const foldersLoaded = React.useRef(false);
  React.useEffect(() => {
    if (foldersLoaded.current) return;
    foldersLoaded.current = true;
    api.folders().then(
      result => dispatch.setFolders(foldersFix(result)),
      () => null
    );
  }, [api, dispatch, folders]);
  
  const getOptions = React.useCallback(() => {
    if (!folders) return [];
    const children = {};
    for (const id in folders) {
      const parent = folders[id].parent || 0;
      children[parent] = (children[parent] || []);
      children[parent].push(parseInt(id));
    }
    const options = [];
    function traverse(id, depth) {
      options.push({id, name: (id === 0 ? _L`My Profiles` : (folders[id].name || folders[id])), depth});
      if (children[id]) {
        for (const child of children[id]) {
          traverse(child, depth + 1);
        }
      }
    }
    traverse(0, 0);
    return options;
  }, [folders]);

  const onChange = React.useCallback(({id}) => setFolder(id), [setFolder]);

  if (!folders) return null;

  const selected = folders[folder];
  const value = selected ? {id: folder, name: selected.name || selected} : {id: 0, name: _L`My Profiles`};

  return (
    <Select options={getOptions}
            renderOption={renderFolder}
            value={value}
            onChange={onChange}
            className="d3p-save-folder"
    />
  );
});

export default function SaveSection() {
  const [status, setStatus] = EditingContext.useState("saveStatus", null);

  const [saveAll, setSaveAll] = React.useState(false);
  const onChangeSaveAll = React.useCallback(e => setSaveAll(e.target.checked), []);

  const [saveName, setSaveName] = React.useState("");
  const onChangeSaveName = React.useCallback(e => setSaveName(e.target.value), []);

  const [saving, setSaving] = React.useState(false);
  const [folder, setFolder] = React.useState(0);

  const dispatch = Store.useDispatch();
  const mainStore = Store.useStore();
  const historyStore = History.useStore();
  const api = useApi();

  const sessionDispatch = Session.useDispatch();

  const statusRef = React.useRef();
  const onFocus = React.useCallback(() => {
    statusRef.current.select();
  }, []);

  const onSave = React.useCallback(() => {
    const state = mainStore.getState();
    const historyState = historyStore.getState();
    let data;
    if (saveAll) {
      data = exportAllProfiles(state, historyState, saveName);
    } else {
      data = state.profiles[state.activeProfile];
      data = exportProfile(data, historyState[data.id], saveName);
    }
    setSaving(true);
    setStatus(null);
    api.save(data, folder ? {folder} : null).then(
      id => {
        const url = new URL(getProfilePath(id), window.location);
        setStatus(url.href);
        if (saveAll || state.profiles.length === 1) {
          window.history?.replaceState({}, "", url.href);
          dispatch.setName(saveName);
        }
        sessionDispatch.setProfiles(profiles => profilesAdd(profiles, data, parseInt(id), folder));
      },
      error => setStatus(error.message),
    ).finally(() => setSaving(false));
  }, [saveAll, saveName, mainStore, historyStore, api, setStatus, dispatch, sessionDispatch, folder]);

  return (
    <div className="d3p-SectionContent d3p-section-save">
      <div className="d3p-description">
        {_L`Enter a name or a short description for your profile. It will show in the page title when the profile is loaded.`}
      </div>
      <div className="d3p-import-options">
        <div className="d3p-save-options">
          <FolderSelect folder={folder} setFolder={setFolder}/>
          <label>
            <input type="checkbox" value={saveAll} onChange={onChangeSaveAll} disabled={saving}/>
            {_L`Save all sets and settings`}
          </label>
        </div>

        <input type="text" className="d3p-save-name" value={saveName} onChange={onChangeSaveName} disabled={saving}/>

        <button onClick={onSave} disabled={saving}>
          {saving && <FontAwesomeIcon icon={faSpinner} spin/>}
          {saving ? _L`Saving` : _L`Save`}
        </button>
      </div>
      {!!status && (
        <div className="d3p-save-status">
          <input type="text" value={status} readOnly ref={statusRef} onFocus={onFocus}/>
        </div>
      )}
    </div>
  );
}
